import { useContext } from "@/store/context";
export const useDateTimeFormat = (locale) => {
    const userLocale = locale ?? useContext().currentLocaleJavaScript;
    const toDate = (date) => {
        if (typeof date == "string") {
            // Date-only ISO strings (yyyy-MM-dd) are parsed as UTC by new Date(),
            // which shifts the displayed date back a day for users in timezones behind UTC.
            // Parse them as local dates instead to preserve the intended calendar date.
            const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
            if (dateOnlyMatch) {
                return new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]));
            }
            return new Date(date);
        }
        return date;
    };
    // Format: Mar 17
    const shortDate = (date) => {
        const options = {
            day: "numeric",
            month: "short"
        };
        return toDate(date).toLocaleDateString(userLocale, options);
    };
    // Format: Mar 17 or Mar 17, 2022 (if current year is not the same as the date provided)
    const shortDateOptionalYear = (date) => {
        const options = {
            day: "numeric",
            month: "short"
        };
        if (toDate(date).getFullYear() != new Date().getFullYear()) {
            options.year = "numeric";
        }
        return toDate(date).toLocaleDateString(userLocale, options);
    };
    // Format: Mar 17, 2022 (always includes year)
    const shortDateWithYear = (date) => {
        const options = {
            day: "numeric",
            month: "short",
            year: "numeric"
        };
        return toDate(date).toLocaleDateString(userLocale, options);
    };
    // Format: Sep 20, 2023 10:46 AM (no 'at' between date and time)
    const shortDateWithHourMinute = (date) => {
        const dateOptions = {
            day: "numeric",
            month: "short",
            year: "numeric"
        };
        const timeOptions = {
            hour: "numeric",
            minute: "numeric"
        };
        const dateString = toDate(date).toLocaleDateString(userLocale, dateOptions);
        const timeString = toDate(date).toLocaleTimeString(userLocale, timeOptions);
        return `${dateString} ${timeString}`;
    };
    // Format: March 6, 2023
    const longDate = (date) => {
        const options = {
            day: "numeric",
            month: "long",
            year: "numeric"
        };
        return toDate(date).toLocaleDateString(userLocale, options);
    };
    // Format: 10:46 AM
    const hourMinuteTime = (date) => {
        const options = {
            hour: "numeric",
            minute: "numeric"
        };
        return toDate(date).toLocaleTimeString(userLocale, options);
    };
    // Format: March 6, 2023 at 10:46 AM
    const longDateWithAtHourMinute = (date) => {
        const options = {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric"
        };
        return toDate(date).toLocaleTimeString(userLocale, options);
    };
    // Format: March 6, 2023 10:46 AM (no 'at' between date and time)
    const longDateWithHourMinute = (date) => {
        const dateOptions = {
            day: "numeric",
            month: "long",
            year: "numeric"
        };
        const timeOptions = {
            hour: "numeric",
            minute: "numeric"
        };
        const dateString = toDate(date).toLocaleDateString(userLocale, dateOptions);
        const timeString = toDate(date).toLocaleTimeString(userLocale, timeOptions);
        return `${dateString} ${timeString}`;
    };
    // Format: 0:05 (for seconds). 1:05:05 ( for > 1 hour)
    const timerFormat = (seconds) => {
        seconds = Math.floor(seconds);
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        let str = h > 0 ? h + ":" : "";
        str += (m < 10 && h > 0 ? "0" : "") + m + ":";
        str += (s < 10 ? "0" : "") + s;
        return str;
    };
    const toISODate = (date) => {
        const d = toDate(date);
        return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
    };
    return {
        shortDate,
        shortDateOptionalYear,
        shortDateWithYear,
        shortDateWithHourMinute,
        longDate,
        hourMinuteTime,
        longDateWithAtHourMinute,
        longDateWithHourMinute,
        timerFormat,
        toISODate
    };
};
